// useWebBlePrint — hook React que ata el transporte Web Bluetooth (BLE), el slice de
// perfiles persistidos y el encoder ESC/POS para imprimir tickets térmicos directo a una
// impresora BLE cuando el panel corre en navegador (NO Electron).
//
// API espejo de `useElectron`: el integrador ramifica los sitios de impresión web a
//   `webBle.isWebBleActive ? webBle.printOrder(...) : pdfMakePrint(...)`.
//
// Reglas críticas:
// - NUNCA se accede a `navigator.bluetooth` en el top-level (rompería jsdom). Todo acceso
//   vive dentro de funciones, detrás de la guarda `isWebBleSupported()`.
// - Antes de CADA impresión se llama `ensureReady` (reconexión silenciosa): las térmicas
//   BLE se duermen entre pedidos. Si no hay conexión viva se intenta `reconnectKnown`
//   (sin diálogo) y, como último recurso, se expone `pairPrinter` para reemparejar dentro
//   de un gesto de usuario.
// - El emparejamiento (`pairPrinter`) abre el chooser del navegador y REQUIERE gesto de
//   usuario; persiste el perfil (deviceId/deviceName) en el slice durable.

import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { OrderDetail } from '@interfaces/OrderTypes';
import { useDetailedOrders } from '@pages/Orders/hooks/orders';
import { appStore } from '@store/index';
import { capturePrintError, reportTicketResidual } from '@utils/print/printSentry';
import { sumSelfServiceAmounts } from '@utils/print/selfServiceTotals';
import { buildWebBleTicket, WebBleTicketOptions } from '@utils/print/webble/buildWebBleTicket';
import { CanvasTicketOptions, renderTicketCanvas } from '@utils/print/webble/canvasTicket';
import { EscPos } from '@utils/print/webble/escpos';
import { useWebBlePrintMode } from '@utils/print/webble/printMode';
import { getLegalDisclaimer } from '@utils/print/webble/legalDisclaimerStore';
import {
  bandImageDataToEscPosRaster,
  canvasToEscPosRaster,
  canvasToEscPosStar,
  dotsForWidth,
  imageDataToEscPosRaster,
  imageToEscPosRaster,
} from '@utils/print/webble/raster';
import {
  attachDevice,
  ensureReady as ensureReadyTransport,
  getConnection,
  isWebBleSupported as isWebBleSupportedTransport,
  reconnectKnown,
  requestPrinter,
  resolveSpeedStep,
  writeChunked,
} from '@utils/print/webble/bleTransport';
import type { BluetoothDevice } from '../../types/web-bluetooth';
import { useMainWebBlePrinter, useWebBlePrintersActions } from '@store/actions/webBlePrinters';
import type { WebBlePageWidth, WebBlePrintSettings } from '@store/slices/webBlePrinters';
import { resolveContentFlags } from '@store/slices/webBlePrinters.types';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { BLE_FEATURE_KEY } from '@utils/featureRollout/constants';
import { recordCoreAction } from '@utils/featureRollout/usageSignals';
import { recordReconnectOutcome } from '@utils/print/webble/reconnectTelemetry';
import { getCommerceLogoDataUri } from '@services/commerceLogoService';

type PrintOrderArgs = {
  detailedOrder?: OrderDetail;
  orderId: string;
  /** Perfil de impresora a usar. Default: la principal (`getMainPrinter`). */
  settings?: WebBlePrintSettings;
  /**
   * Permite abrir un chooser FILTRADO (solo la impresora guardada) como último recurso
   * cuando la reconexión silenciosa falla (p.ej. tras recargar la página y `getDevices`
   * no devuelve el dispositivo). REQUIERE gesto de usuario → solo `true` en impresión
   * manual (botón). En auto-impresión por socket NO hay gesto, así que va en `false`.
   */
  allowPrompt?: boolean;
  /** Clave del perfil, para persistir el nuevo deviceId si se reconecta vía chooser. */
  printerKey?: string;
  /** Interno: dentro de un lote multi-impresora; NO togglear `isPrinting` (lo dueña el lote). */
  keepBatchState?: boolean;
};

// Resultado de printOrder: SOLO señal para el caller (NO cambia los bytes impresos).
// `ok` = la escritura BLE se completó; `degradedToText` = el formato imagen no se pudo
// construir y se imprimió en texto. Lo consume la UI de "Probar impresión" (C-03).
export type WebBlePrintResult = { ok: boolean; degradedToText: boolean };

type PrintSelfServiceArgs = {
  orderIDs: string[];
  settings?: WebBlePrintSettings;
  allowPrompt?: boolean;
  printerKey?: string;
  keepBatchState?: boolean;
};

// Resultado de intentar resolver (reconectar) un dispositivo:
// - 'connected': hay un device listo para escribir.
// - 'cancelled': el usuario cerró el chooser (NotFoundError/AbortError) → silencio.
// - 'failed': la reconexión falló por otra razón (impresora apagada, chooser sin
//   resultados, sin gesto disponible) → el caller debe avisar al usuario.
type ResolveOutcome = { status: 'connected'; device: BluetoothDevice } | { status: 'cancelled' } | { status: 'failed' };

// Prefijo de nombre estable para filtrar el chooser (las térmicas suelen ser "PT-220_B49C").
const namePrefixOf = (deviceName?: string | null): string | undefined => {
  const name = (deviceName || '').trim();
  if (!name) return undefined;
  return name.split('_')[0] || name;
};

type PairPrinterArgs = {
  /** Perfil al que asignar el dispositivo. Si se omite, se crea uno nuevo. */
  printerKey?: string;
  /** Prefijo de nombre para filtrar el chooser; si se omite, acepta todos. */
  namePrefix?: string;
};

// Starting name for a profile: whatever the device advertises, or a generic one when it advertises
// nothing. This is a SIGN-UP default, not a choice the commerce made — hence the pairing with
// `isDefaultLabel`.
const defaultLabelForDevice = (deviceName?: string | null): string => deviceName ?? 'Impresora Bluetooth';

// Is the profile name still the default this very code wrote when the profile was created? That is
// the question deciding whether re-pairing may overwrite it. It is answered by FIXED POINT: the
// label belongs to nobody if it is exactly what `defaultLabelForDevice` would have produced for the
// device the profile had stored (or if it is empty). This covers all three provenances:
//   1. Typed into "Nombre (opcional)" ("Mostrador") → differs → untouchable.
//   2. Proposed by the wizard ("Caja (PT-220)", `suggestPrinterName`) → differs → untouchable: the
//      operator did not type it, but it encodes the use they DID choose.
//   3. The raw device name ("PT-220_B49C") or the generic one → matches → nobody chose anything, and
//      keeping it after a hardware swap would leave the profile named after the old printer.
// Accepted limit: whoever types a name identical to the device's own falls into (3). Telling them
// apart would require persisting the label's provenance, and already-stored profiles do not carry
// it — backfilling such a flag would have to be this very comparison.
const isDefaultLabel = (label?: string | null, deviceName?: string | null): boolean => {
  const current = (label ?? '').trim();
  return !current || current === defaultLabelForDevice(deviceName);
};

// Construye las opciones de ticket a partir del perfil de impresora + datos del comercio.
const buildTicketOptions = (settings: WebBlePrintSettings): WebBleTicketOptions => {
  const commerce = appStore.getState().commerce;
  return {
    ticketType: settings.ticketType,
    textSize: settings.textSize,
    pageWidth: settings.pageWidth,
    decoration: settings.decoration,
    commerceName: commerce?.nombre,
    commerceAddress: commerce?.address?.fullAddress || commerce?.direccion || '',
    referencesLabel: commerce?.config_form_entrega?.referencias?.label,
    showClientPhone: settings.showClientPhone,
    showCommerceName: settings.showCommerceName,
    showCommerceAddress: settings.showCommerceAddress,
    // Flags de contenido RESUELTOS desde el perfil (preset del rol + overrides explícitos del
    // usuario). Para un perfil legacy (sin flags ricos) resolveContentFlags devuelve el preset
    // del rol → salida BYTE-IDÉNTICA a la histórica (probado por content.equiv.test.ts). Una vez
    // que el usuario toca un toggle rico, el IR lo honra sobre el MISMO pipeline.
    content: resolveContentFlags(settings),
    legalDisclaimer: getLegalDisclaimer(commerce?.id),
  };
};

// Opciones para el renderer a canvas (imagen): mismas que el ticket de texto +
// `showLogo` y `logoUrl` (el renderer carga y dibuja el logo internamente).
const buildCanvasOptions = (settings: WebBlePrintSettings): CanvasTicketOptions => {
  const commerce = appStore.getState().commerce;
  return {
    ticketType: settings.ticketType,
    textSize: settings.textSize,
    pageWidth: settings.pageWidth,
    decoration: settings.decoration,
    showLogo: settings.showLogo,
    commerceName: commerce?.nombre,
    commerceAddress: commerce?.address?.fullAddress || commerce?.direccion || '',
    logoUrl: commerce?.logotipo ?? null,
    referencesLabel: commerce?.config_form_entrega?.referencias?.label,
    showClientPhone: settings.showClientPhone,
    showCommerceName: settings.showCommerceName,
    showCommerceAddress: settings.showCommerceAddress,
    // Mismos flags ricos que el ticket de texto → preview/print de imagen HONRA la config.
    content: resolveContentFlags(settings),
    legalDisclaimer: getLegalDisclaimer(commerce?.id),
  };
};

// Líneas de avance antes del corte (igual que el feedBeforeCut del encoder de texto).
const FEED_BEFORE_CUT = 4;

// Envuelve el raster (GS v 0 del canvas) en un ticket imprimible completo:
// ESC @ (reset) al inicio, el raster, y feed + corte al final. El corte va SIEMPRE
// DESPUÉS del raster (de lo contrario se cortaría sobre la imagen).
const wrapRasterTicket = (raster: Uint8Array): Uint8Array =>
  new EscPos().raw(raster).feed(FEED_BEFORE_CUT).cut().encode();

// Genera un buffer RGBA opaco NEGRO de w×h (recuadro sólido) para el auto-test.
const makeBlackBox = (w: number, h: number): Uint8ClampedArray => {
  const data = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < w * h; i++) {
    data[i * 4 + 3] = 255; // RGB=0 (negro), alpha=255 (opaco)
  }
  return data;
};

// AUTO-TEST v2 (un solo tiro): ya confirmamos que la PT-220 hace GS v 0 con un recuadro angosto+
// chico pero el ticket real (ancho completo + grande) sale basura. Este test AÍSLA la causa con
// recuadros GS v 0 que varían UNA dimensión a la vez:
//   B) angosto (200) + chico (24)  → control (debe salir barra).
//   C) ANCHO completo (dots) + chico (24) → testea el ANCHO (48 bytes/fila @384).
//   D) angosto (200) + GRANDE (240, 10 bandas de 24, ~6KB) → testea el TAMAÑO/transporte BLE.
// El usuario reporta cuál sale como barra sólida vs caracteres: si C falla → es el ancho; si D
// falla → es el transporte soltando bytes en envíos grandes (encaja con que el texto, chico, sí).
const buildPrinterDiagnostic = (pageWidth: WebBlePageWidth): Uint8Array => {
  const dots = dotsForWidth(pageWidth); // 384 (58mm) / 576 (80mm)
  const narrow = Math.min(200, dots);
  const tall = 240;
  const p = new EscPos();
  p.align(1).bold(true).line('PLICK DIAG v2').bold(false);
  p.line(`ancho activo: ${pageWidth}mm (${dots})`);
  p.align(0);
  p.line('');
  p.line('A) texto control:');
  p.line('   ABCabc 0123 -- OK');
  p.line('');
  p.line(`B) GSv0 ${narrow}x24 angosto+chico:`);
  p.raw(imageDataToEscPosRaster(makeBlackBox(narrow, 24), narrow, 24)).feed(1);
  p.line(`C) GSv0 ${dots}x24 ANCHO+chico:`);
  p.raw(imageDataToEscPosRaster(makeBlackBox(dots, 24), dots, 24)).feed(1);
  p.line(`D) GSv0 ${narrow}x${tall} banda24 GRANDE:`);
  p.raw(bandImageDataToEscPosRaster(makeBlackBox(narrow, tall), narrow, tall, 24)).feed(1);
  p.line('--- FIN DIAG ---');
  return p.feed(FEED_BEFORE_CUT).cut().encode();
};

// Alto de banda del raster imagen (GS v 0 troceado). Muchas térmicas BLE genéricas (PT-220)
// no imprimen un GS v 0 alto → el ticket sale ~2px por línea. Troceando en bandas se arregla.
// El valor exacto es model-dependent → testeable sin redeploy vía localStorage
// 'webBleRasterBandHeight' (panel debug). Default 24 (Epson 24-dot, el más compatible). 0 =
// sin bandas (un solo GS v 0, comportamiento previo).
const DEFAULT_RASTER_BAND_HEIGHT = 24;
const getRasterBandHeight = (): number => {
  try {
    const v = window.localStorage?.getItem('webBleRasterBandHeight');
    const n = v ? parseInt(v, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_RASTER_BAND_HEIGHT;
  } catch {
    return DEFAULT_RASTER_BAND_HEIGHT;
  }
};

// Modo de raster imagen, conmutable sin redeploy vía localStorage 'webBleRasterMode' (panel
// debug): 'gsv0' (GS v 0, default) o 'escstar' (ESC * bit-image 24-dot). Muchas térmicas baratas
// (PT-220) NO honran GS v 0 e imprimen su payload como TEXTO; ESC * es el comando de gráficos
// clásico, mejor soportado. A/B en hardware para decidir el default por modelo.
const getRasterMode = (): 'gsv0' | 'escstar' => {
  try {
    return window.localStorage?.getItem('webBleRasterMode') === 'escstar' ? 'escstar' : 'gsv0';
  } catch {
    return 'gsv0';
  }
};

// Intenta construir el ticket como IMAGEN (canvas → 1-bit → GS v 0). Devuelve null
// si el entorno no tiene DOM, el logo tainteó por CORS, o algo falló — en cuyo caso
// el caller cae al encoder de texto (buildWebBleTicket).
const tryBuildCanvasBytes = async (order: OrderDetail, settings: WebBlePrintSettings): Promise<Uint8Array | null> => {
  const opts = buildCanvasOptions(settings);
  // Plan B: el modo IMAGEN también carga el logo desde el dataUri base64 (sin CORS), no
  // desde la URL del CDN — si no, el logo del print imagen tainteaba y caía a texto.
  if (opts.showLogo && opts.logoUrl) {
    const commerce = appStore.getState().commerce;
    opts.logoUrl = await getCommerceLogoDataUri(commerce?.id, commerce?.logotipo);
  }
  const canvas = await renderTicketCanvas(order, opts);
  if (!canvas) return null;
  // ESC * (bit-image) o GS v 0 (bandas) según el modo elegido — el A/B en hardware decide cuál
  // materializa el modelo. ESC * lleva conteo de bytes explícito → no desborda como texto.
  const raster =
    getRasterMode() === 'escstar' ? canvasToEscPosStar(canvas) : canvasToEscPosRaster(canvas, getRasterBandHeight()); // troceado en bandas (compat PT-220)
  if (!raster) return null;
  return wrapRasterTicket(raster);
};

// Cache del logo (raster ESC/POS) por URL+ancho: evita re-descargar/convertir en cada
// impresión. Devuelve null si no hay logo, está apagado, o la imagen no es accesible.
const logoRasterCache = new Map<string, Uint8Array | null>();

const resolveLogoRaster = async (printer: WebBlePrintSettings): Promise<Uint8Array | null> => {
  if (!printer.showLogo) return null;
  const commerce = appStore.getState().commerce;
  const logotipoUrl = commerce?.logotipo;
  if (!logotipoUrl) return null;
  // Plan B: el logo sale del endpoint base64 (sin CORS), NO de la URL del CDN. El data: URI
  // resultante NO taintea el canvas → imageToEscPosRaster puede leer getImageData y rasterizar.
  const dataUri = await getCommerceLogoDataUri(commerce?.id, logotipoUrl);
  if (!dataUri) return null; // sin logo (404/502/red) → ticket sin logo, no rompe impresión.
  const dots = dotsForWidth(printer.pageWidth);
  // Cache de raster en memoria keyada por id+dots+logotipoUrl: usamos logotipoUrl (estable y
  // corto) en lugar del dataUri (evita keys base64 de cientos de KB). Es correcta porque el
  // dataUri se invalida por logotipoUrl aguas arriba (getCommerceLogoDataUri). El id del comercio
  // evita que dos comercios con la MISMA URL compartan raster (patrón frágil del bug PR#1020).
  const key = `${commerce?.id ?? ''}|${dots}|${logotipoUrl}`;
  const cached = logoRasterCache.get(key);
  if (cached !== undefined) return cached;
  const raster = await imageToEscPosRaster(dataUri, dots); // rasteriza DESDE el dataUri base64.
  // Solo cachear ÉXITOS: un null (fallo transitorio del rasterizado) cacheado dejaría el
  // ticket SIN logo el resto de la sesión; sin cachearlo, la próxima impresión reintenta (Seer).
  if (raster !== null) logoRasterCache.set(key, raster);
  return raster;
};

export const useWebBlePrint = () => {
  const [isPrinting, setIsPrinting] = useState(false);
  const { getOrder } = useDetailedOrders();
  const mainPrinter = useMainWebBlePrinter();
  const { updateWebBlePrintSettings, addPrinter } = useWebBlePrintersActions();

  // Telemetría de funnel a GA4 (PostHog off). `trackEvent` de useTracking devuelve nueva
  // referencia por render; lo guardamos en un ref para leer el closure más reciente desde las
  // funciones async sin desestabilizar nada. Patrón de useFeatureVersion (reportado por Seer).
  const { trackEvent } = useTracking();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  // Emisión fire-and-forget: el funnel NUNCA debe romper la impresión. Cualquier error de
  // telemetría se traga (Sentry sigue siendo la fuente de verdad para errores de impresión).
  const emitFunnel = (eventName: string, params?: Record<string, unknown>): void => {
    try {
      trackEventRef.current(eventName, params);
    } catch {
      // no-op: la telemetría no debe interferir con la impresión.
    }
  };

  // `isWebBleActive`: el usuario activó el modo Bluetooth (master toggle, opt-in), hay
  // soporte del navegador Y una impresora habilitada y emparejada (deviceId/deviceName
  // para reconectar sin diálogo). Si es false, el sitio de impresión cae al fallback
  // pdfMake (diálogo del navegador) — comportamiento tradicional.
  const { enabled: isBlePrintModeOn } = useWebBlePrintMode();
  const hasPairedPrinter = Boolean(mainPrinter?.enabled && (mainPrinter?.deviceId || mainPrinter?.deviceName));
  const isWebBleActive = isWebBleSupportedTransport() && isBlePrintModeOn && hasPairedPrinter;

  // Ref siempre-fresco de la impresora principal: printOrder/printSelfService pueden invocarse
  // desde callbacks async estables (auto-print por socket) que capturaron un closure viejo —
  // sin el ref, un cambio de impresora principal a media sesión mandaría el trabajo a la
  // anterior (Seer). Mismo patrón que trackEventRef.
  const mainPrinterRef = useRef(mainPrinter);
  mainPrinterRef.current = mainPrinter;

  const getMainPrinter = (): WebBlePrintSettings | undefined => mainPrinterRef.current;

  // Reconexión ASISTIDA (requiere gesto de usuario): abre el chooser FILTRADO al nombre guardado
  // —aparece SOLO su impresora, un toque la reconecta— y refresca el handle del perfil. Extraída
  // para tener UNA sola definición (chooser + persistencia del deviceId + telemetría) que consumen
  // sus DOS entradas: la reconexión silenciosa devolvió null (no encontró la impresora) o RECHAZÓ
  // (el handle guardado ya no conecta). `after` etiqueta cuál fue, para que el triage separe las
  // etapas de un mismo toque. NUNCA crea un perfil: actualiza el de `printerKey`.
  const promptForKnownPrinter = async (
    settings: WebBlePrintSettings,
    opts: { printerKey?: string },
    after: 'silent_empty' | 'silent_error',
  ): Promise<ResolveOutcome> => {
    try {
      const prefix = namePrefixOf(settings.deviceName);
      const device = await requestPrinter(prefix ? { namePrefix: prefix } : {});
      // El chooser YA se abrió: aquí no queda activación de usuario que preservar, así que este
      // attach conserva los reintentos del transporte (suben la tasa de éxito con la térmica
      // recién despierta). Los reintentos solo se recortan ANTES del chooser, no después.
      await attachDevice(device);
      // El deviceId de Web Bluetooth es volátil entre sesiones: persistir el nuevo para
      // que la PRÓXIMA reconexión silenciosa (getDevices) tenga más chance de acertar.
      if (opts.printerKey) {
        updateWebBlePrintSettings(
          { deviceId: device.id, deviceName: device.name ?? settings.deviceName },
          opts.printerKey,
        );
      }
      // Reconexión ASISTIDA exitosa (chooser filtrado dentro de un gesto de usuario).
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectSuccess, { via: 'prompt', after });
      recordReconnectOutcome({ via: 'prompt', status: 'success', at: Date.now() });
      return { status: 'connected', device };
    } catch (error) {
      // Cancelar el chooser (NotFoundError/AbortError) no es una falla real → silencio.
      const name = error instanceof Error ? error.name : '';
      if (name === 'NotFoundError' || name === 'AbortError') {
        recordReconnectOutcome({ via: 'prompt', status: 'cancelled', at: Date.now() });
        return { status: 'cancelled' };
      }
      capturePrintError(error, { tag: 'web_ble_reconnect_prompt_failure', trigger: 'web_ble', extra: { after } });
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectFailed, { via: 'prompt', reason: name || 'unknown', after });
      recordReconnectOutcome({ via: 'prompt', status: 'failed', at: Date.now() });
      return { status: 'failed' };
    }
  };

  // Reconecta (silenciosamente) o reutiliza una conexión viva para un perfil dado y
  // devuelve el device listo. Estrategia (espejo del spike probado contra hardware):
  //   1. Conexión viva en runtime (mismo dispositivo ya abierto).
  //   2. Reconexión SIN diálogo: handle en memoria (misma sesión) o getDevices().
  //   3. Último recurso (solo con gesto de usuario): chooser FILTRADO al nombre guardado
  //      → aparece SOLO su impresora, un toque la reconecta. Cubre el caso "recargué la
  //      página / cerré sesión y getDevices no la devuelve" sin re-emparejar a ciegas, y
  //      también el caso "getDevices SÍ la devuelve pero ese handle ya no conecta".
  // DISTINGUE el resultado (connected/cancelled/failed) para que el caller decida el feedback:
  // 'cancelled' (el usuario cerró el chooser) NO debe tratarse como fallo (sin toast/Sentry/
  // métrica de fallo). Todos los call-sites (printBytes, reconnectPrinter, silentReconnect) lo
  // consumen directo.
  const resolveDeviceOutcome = async (
    settings: WebBlePrintSettings,
    opts: { allowPrompt?: boolean; printerKey?: string } = {},
  ): Promise<ResolveOutcome> => {
    if (settings.deviceId) {
      const live = getConnection(settings.deviceId);
      if (live && live.device.gatt?.connected) return { status: 'connected', device: live.device };
    }
    // La reconexión silenciosa puede RECHAZAR (no solo devolver null): `reconnectKnown` termina
    // en `attachDevice` → `gatt.connect()`, que lanza NetworkError ("Bluetooth Device is no longer
    // in range") con la impresora apagada, fuera de alcance o —el caso de este fix— con un handle
    // guardado que ya murió. Ese rechazo escapaba de esta función —rompiendo su contrato
    // `Promise<ResolveOutcome>`— y llegaba a la consola como "Uncaught (in promise)" desde los
    // onClick de Ajustes. Aquí lo MODELAMOS, y el desenlace depende de si hay gesto de usuario:
    //
    // - CON gesto (Confirmar, impresión manual) el rechazo NO es el final: se escala al chooser.
    //   Medido en producción (PT-220_B49C, "Por confirmar"): Confirmar fallaba y "Cambiar
    //   impresora" —que va al chooser— conectaba la MISMA impresora al instante. La diferencia es
    //   el handle: el camino silencioso reusa el de memoria o el de `getDevices()`, que entre
    //   sesiones queda stale y su `gatt.connect()` no levanta nunca; `requestDevice` entrega uno
    //   FRESCO que sí conecta. Un comentario anterior afirmaba aquí lo contrario ("el chooser
    //   tampoco lograría conectar"): era suposición razonada, no medida, y la evidencia la refutó.
    //   Para que el chooser sea alcanzable hay que llegar con la activación de usuario VIVA, así
    //   que este intento silencioso pide UN solo intento de GATT (`attempts: 1`): los 3 del default
    //   queman >750ms solo en esperas y el `requestDevice` posterior moriría con SecurityError.
    // - SIN gesto (warm-up de Ajustes, reconexión de fondo) no hay activación que preservar y los
    //   reintentos SÍ valen (la térmica recién despierta rechaza el primer connect): default
    //   intacto y el fallo termina en 'failed', igual que la rama `no_gesture`. El warm-up no
    //   interrumpe al usuario con un diálogo que él no pidió.
    //
    // Aquí no existe el caso "el usuario canceló": ningún diálogo se abrió todavía.
    let reconnected: Awaited<ReturnType<typeof reconnectKnown>> = null;
    try {
      reconnected = await reconnectKnown({
        deviceId: settings.deviceId ?? undefined,
        deviceName: settings.deviceName ?? undefined,
        attempts: opts.allowPrompt ? 1 : undefined,
      });
    } catch (error) {
      const reason = error instanceof Error ? error.name || 'unknown' : 'unknown';
      // `allow_prompt` distingue en el triage el warm-up de Ajustes (false) del botón Confirmar
      // o de una impresión manual (true); el fingerprint agrupa todo bajo un mismo issue. Con
      // gesto este evento es la PRIMERA etapa del mismo toque: le sigue el del chooser, que va
      // con `via:'prompt'` + `after:'silent_error'` (tag distinto en Sentry) — así el triage
      // separa "falló y se rindió" de "falló y el chooser lo rescató".
      capturePrintError(error, {
        tag: 'web_ble_reconnect_silent_failure',
        trigger: 'web_ble',
        extra: { allow_prompt: Boolean(opts.allowPrompt) },
      });
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectFailed, { via: 'silent', reason });
      recordReconnectOutcome({ via: 'silent', status: 'failed', at: Date.now() });
      if (!opts.allowPrompt) return { status: 'failed' };
      return promptForKnownPrinter(settings, opts, 'silent_error');
    }
    if (reconnected?.device) {
      // La reconexión silenciosa puede emparejar por NOMBRE un device con id distinto
      // al guardado (el deviceId de Web Bluetooth es volátil entre sesiones). Si difiere,
      // persistir el nuevo id para que la próxima reconexión por getDevices acierte directo.
      if (opts.printerKey && reconnected.device.id && reconnected.device.id !== settings.deviceId) {
        updateWebBlePrintSettings(
          { deviceId: reconnected.device.id, deviceName: reconnected.device.name ?? settings.deviceName },
          opts.printerKey,
        );
      }
      // Reconexión SILENCIOSA exitosa (handle en memoria / getDevices, sin chooser).
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectSuccess, { via: 'silent' });
      recordReconnectOutcome({ via: 'silent', status: 'success', at: Date.now() });
      return { status: 'connected', device: reconnected.device };
    }

    // Sin gesto de usuario no se puede abrir el chooser: la reconexión silenciosa falló.
    if (!opts.allowPrompt) {
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectFailed, { via: 'silent', reason: 'no_gesture' });
      recordReconnectOutcome({ via: 'silent', status: 'failed', at: Date.now() });
      return { status: 'failed' };
    }
    // Reconexión asistida (requiere gesto): chooser filtrado a la impresora guardada.
    return promptForKnownPrinter(settings, opts, 'silent_empty');
  };

  // Imprime los bytes de un ticket ya construido, respetando `copies`. Centraliza
  // ensureReady + writeChunked + manejo de errores.
  const printBytes = async (
    settings: WebBlePrintSettings,
    bytes: Uint8Array,
    opts: { allowPrompt?: boolean; printerKey?: string; degradedToText?: boolean } = {},
  ): Promise<boolean> => {
    // `format` define el slice del funnel de impresión (imagen vs texto). El error_rate del
    // rollout = BlePrintFailure / (BlePrintSuccess + BlePrintFailure) por formato.
    const format = settings.ticketFormat;
    // Cuando el formato era imagen y cayó al encoder de texto (CORS/sin DOM), se adjunta a
    // TODOS los eventos del funnel de esta impresión sin contar un fallo extra.
    const degraded = opts.degradedToText ? { degraded_to_text: true } : {};
    if (!isWebBleSupportedTransport()) {
      capturePrintError(new Error('Web Bluetooth no soportado'), {
        tag: 'web_ble_not_supported',
        trigger: 'web_ble',
      });
      emitFunnel(TRACK_EVENT_NAMES.BlePrintFailure, { format, reason: 'not_supported', ...degraded });
      return false;
    }
    const outcome = await resolveDeviceOutcome(settings, opts);
    if (outcome.status !== 'connected') {
      // 'cancelled' (el usuario cerró el chooser) NO es un fallo real → silencio total: sin
      // toast, sin Sentry y SIN BlePrintFailure (no inflar el error_rate del rollout con
      // cancelaciones voluntarias). Solo 'failed' (impresora apagada / sin gesto) avisa.
      if (outcome.status === 'failed') {
        capturePrintError(new Error('Impresora BLE no emparejada/reconectable'), {
          tag: 'web_ble_printer_not_paired',
          trigger: 'web_ble',
        });
        // Mensaje según haya o no gesto: en manual sugiere reconectar tocando; en auto avisa
        // que la impresora no respondió (no se puede abrir chooser sin gesto).
        toast.error(
          opts.allowPrompt
            ? 'No se pudo conectar la impresora Bluetooth. Enciéndela y vuelve a intentar.'
            : 'La impresora Bluetooth no respondió. Imprime manualmente o revísala en Ajustes.',
        );
        emitFunnel(TRACK_EVENT_NAMES.BlePrintFailure, { format, reason: 'not_connected', ...degraded });
      }
      return false;
    }
    const device = outcome.device;
    // Core-action del rollout impresion-ble: intento real de impresión (ya con dispositivo
    // conectado, antes de escribir). NOTA: tras un revert el runtime sigue imprimiendo pero
    // variant=control → recordCoreAction no cuenta (correcto: mide uso de la versión OFRECIDA).
    recordCoreAction(BLE_FEATURE_KEY, 'print_attempt');
    // Techo de 5 copias: protege contra valores corruptos/excesivos en el perfil.
    const copies = Math.min(5, Math.max(1, settings.copies || 1));
    try {
      const writeChar = await ensureReadyTransport(device);
      // Velocidad POR IMPRESORA (slider): el paso del perfil → par (chunk, respiro). Cada modelo
      // topa distinto; el override de debug (localStorage) aún gana para calibrar en hardware.
      const transport = resolveSpeedStep(settings.speedStep);
      // Modo fluido POR IMPRESORA (auto-tuning, regla d): fuerza la ruta con confirmación de la
      // impresora. `undefined` deja la derivación histórica del transporte intacta.
      const writeWithResponse = settings.bleWriteWithResponse;
      for (let i = 0; i < copies; i++) {
        await writeChunked(writeChar, bytes, { ...transport, writeWithResponse });
      }
    } catch (error) {
      // La escritura BLE falló (impresora apagada a mitad, GATT caído, ensureReady sin char).
      // Emitimos el fallo del funnel y RE-LANZAMOS: el caller (printOrder/printSelfServiceOrder)
      // ya lo captura en Sentry y avisa al usuario. No duplicamos esa lógica aquí.
      const reason = error instanceof Error ? error.name || 'WriteError' : 'WriteError';
      emitFunnel(TRACK_EVENT_NAMES.BlePrintFailure, { format, reason, ...degraded });
      throw error;
    }
    emitFunnel(TRACK_EVENT_NAMES.BlePrintSuccess, { format, copies, ...degraded });
    // Core-action: la escritura BLE se completó (mismo punto que BlePrintSuccess del funnel).
    recordCoreAction(BLE_FEATURE_KEY, 'print_success');
    return true;
  };

  const printOrder = async ({
    detailedOrder,
    orderId,
    settings,
    allowPrompt,
    printerKey,
    keepBatchState,
  }: PrintOrderArgs): Promise<WebBlePrintResult> => {
    const printer = settings ?? mainPrinterRef.current;
    if (!printer) return { ok: false, degradedToText: false };
    const opts = { allowPrompt, printerKey };
    try {
      if (!keepBatchState) setIsPrinting(true);
      const order = detailedOrder || (await getOrder(orderId));
      // Aviso a Sentry si el desglose del ticket no cuadra con el total que se va a imprimir.
      reportTicketResidual(order, 'web_ble');
      // Camino preferido: ticket renderizado a IMAGEN (canvas → raster GS v 0), para
      // que se vea igual a Electron/pdfMake y soporte los 5 niveles de tamaño por
      // elemento. Si no hay DOM o el logo taintea por CORS, cae al encoder de texto.
      // El formato configurado era IMAGEN pero el canvas no se pudo construir (sin DOM o logo
      // tainteado por CORS) → caemos al encoder de TEXTO. `degradedToText` se adjunta al evento
      // final del funnel (éxito/fallo) en vez de emitir un fallo propio: así NO inflamos el
      // error_rate (attempts == jobs), pero el rollout ve cuántas impresiones perdieron la imagen.
      // Además se DEVUELVE al caller (sin tocar los bytes): la UI de "Probar" usa este flag para
      // avisar "imprimimos en texto" cuando el formato imagen no pudo construirse (C-03).
      let degradedToText = false;
      if (printer.ticketFormat === 'image') {
        const canvasBytes = await tryBuildCanvasBytes(order, printer);
        if (canvasBytes) {
          const ok = await printBytes(printer, canvasBytes, opts);
          return { ok, degradedToText: false };
        }
        degradedToText = true;
      }
      const logoRaster = await resolveLogoRaster(printer);
      const bytes = buildWebBleTicket(order, { ...buildTicketOptions(printer), logoRaster });
      const ok = await printBytes(printer, bytes, { ...opts, degradedToText });
      return { ok, degradedToText };
    } catch (error) {
      capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble', extra: { orderId } });
      toast.error('Error al imprimir el pedido por Bluetooth');
      return { ok: false, degradedToText: false };
    } finally {
      if (!keepBatchState) setIsPrinting(false);
    }
  };

  const printSelfServiceOrder = async ({
    orderIDs,
    settings,
    allowPrompt,
    printerKey,
    keepBatchState,
  }: PrintSelfServiceArgs): Promise<void> => {
    const printer = settings ?? mainPrinterRef.current;
    if (!printer) return;
    const opts = { allowPrompt, printerKey };
    try {
      if (!keepBatchState) setIsPrinting(true);
      const response = await Promise.all(orderIDs.map((id) => getOrder(id)));
      if (!response || response.length === 0) {
        toast.error('No se encontraron pedidos para imprimir');
        return;
      }
      const sorted = response.sort((a, b) => {
        const aDate = new Date(a?.pedido?.created_at || 0);
        const bDate = new Date(b?.pedido?.created_at || 0);
        return aDate.getTime() - bDate.getTime();
      });
      const tableNumber = sorted[0].pedido?.self_service?.table_number;
      const orderData: OrderDetail = {
        pedido: {
          ...sorted[0].pedido,
          folio: tableNumber ? `mesa: ${tableNumber}` : 'mesa: —',
          numero_mesa: tableNumber,
          // Los cinco montos se SUMAN (incluidos propina y costo_envio, que antes salían del
          // spread del primer pedido y perdían la propina de los pedidos 2..N).
          ...sumSelfServiceAmounts(sorted),
          comentario: '',
        },
        productos: sorted.map((order) => order.productos).flat(),
      };
      // Aviso a Sentry si el desglose del ticket no cuadra con el total que se va a imprimir.
      reportTicketResidual(orderData, 'web_ble');
      // Misma degradación imagen→texto que printOrder: el flag viaja al evento final del funnel.
      let degradedToText = false;
      if (printer.ticketFormat === 'image') {
        const canvasBytes = await tryBuildCanvasBytes(orderData, printer);
        if (canvasBytes) {
          await printBytes(printer, canvasBytes, opts);
          return;
        }
        degradedToText = true;
      }
      const logoRaster = await resolveLogoRaster(printer);
      const bytes = buildWebBleTicket(orderData, { ...buildTicketOptions(printer), logoRaster });
      await printBytes(printer, bytes, { ...opts, degradedToText });
    } catch (error) {
      capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble', extra: { orderIDs } });
      toast.error('Error al imprimir los pedidos por Bluetooth');
    } finally {
      if (!keepBatchState) setIsPrinting(false);
    }
  };

  // Lista de [printerKey, settings] de TODAS las impresoras habilitadas y emparejadas
  // (con deviceId o deviceName) leídas FRESCAS del store. Es el mismo filtro que usa el
  // auto-print por socket (useIncomingOrder): cada impresora imprime su propio formato.
  const enabledPairedPrinters = (): Array<[string, WebBlePrintSettings]> => {
    const printers = appStore.getState().webBlePrinters ?? {};
    return Object.entries(printers).filter(([, p]) => p.enabled && (p.deviceId || p.deviceName)) as Array<
      [string, WebBlePrintSettings]
    >;
  };

  // Impresión MANUAL multi-impresora: itera TODAS las impresoras habilitadas y emparejadas
  // y manda el pedido a cada una con su propio perfil (ticketType/formato/copias). Replica
  // el modelo del auto-print para que, con Caja+Cocina, ambas reciban en manual (printOrder
  // a secas usa solo la principal). Si no hay ninguna, no-op.
  const printOrderToAll = async ({
    detailedOrder,
    orderId,
    allowPrompt,
  }: {
    detailedOrder?: OrderDetail;
    orderId: string;
    allowPrompt?: boolean;
  }): Promise<void> => {
    const targets = enabledPairedPrinters();
    if (!targets.length) return;
    // `isPrinting` se mantiene encendido durante TODO el lote (no parpadea entre impresoras).
    try {
      setIsPrinting(true);
      for (const [printerKey, settings] of targets) {
        await printOrder({ detailedOrder, orderId, settings, printerKey, allowPrompt, keepBatchState: true });
      }
    } finally {
      setIsPrinting(false);
    }
  };

  // Versión multi-impresora de la impresión de autoservicio (mesa). Mismo modelo que
  // `printOrderToAll`: una copia por cada impresora habilitada y emparejada.
  const printSelfServiceToAll = async ({
    orderIDs,
    allowPrompt,
  }: {
    orderIDs: string[];
    allowPrompt?: boolean;
  }): Promise<void> => {
    const targets = enabledPairedPrinters();
    if (!targets.length) return;
    try {
      setIsPrinting(true);
      for (const [printerKey, settings] of targets) {
        await printSelfServiceOrder({ orderIDs, settings, printerKey, allowPrompt, keepBatchState: true });
      }
    } finally {
      setIsPrinting(false);
    }
  };

  // Abre el chooser del navegador (REQUIERE gesto de usuario), conecta el dispositivo y
  // persiste deviceId/deviceName en el perfil indicado (o crea uno nuevo). Devuelve el
  // perfil resultante o null si el usuario canceló / falló la conexión.
  const pairPrinter = async ({ printerKey, namePrefix }: PairPrinterArgs = {}): Promise<WebBlePrintSettings | null> => {
    if (!isWebBleSupportedTransport()) {
      capturePrintError(new Error('Web Bluetooth no soportado'), {
        tag: 'web_ble_not_supported',
        trigger: 'web_ble',
      });
      toast.error('Este navegador no soporta impresión por Bluetooth.');
      return null;
    }
    // Intento de emparejamiento: el usuario abrió el chooser (dentro de un gesto). Marca el
    // denominador de la tasa de éxito de emparejar (success/cancelled del funnel de pairing).
    emitFunnel(TRACK_EVENT_NAMES.BlePairAttempt);
    try {
      const device = await requestPrinter(namePrefix ? { namePrefix } : {});
      await attachDevice(device);
      // `label` does NOT belong here: `pairPrinter` also runs on every RE-pairing ("Cambiar
      // impresora", "No aparece mi impresora", the wizard's "Elegir otra impresora"), and writing it
      // unconditionally renamed back to `PT-220_B49C` the profile the commerce had called
      // "Mostrador" — the exact opposite of what the copy promises ("no se pierde tu configuración";
      // the name IS configuration). Each branch below decides whether it gets to write one.
      const patch: Partial<WebBlePrintSettings> = {
        deviceId: device.id,
        deviceName: device.name ?? null,
        enabled: true,
      };
      // Emparejamiento exitoso: dispositivo elegido y GATT conectado (attachDevice OK).
      emitFunnel(TRACK_EVENT_NAMES.BlePairSuccess, { device_name: device.name ?? null });
      // Core-action del rollout impresion-ble: emparejar una impresora es uso real de la
      // versión nueva (solo cuenta si la feature está en pantalla: treatment o preview).
      recordCoreAction(BLE_FEATURE_KEY, 'printer_paired');
      toast.success(`Impresora ${device.name ?? 'Bluetooth'} conectada.`);
      // Estado FRESCO del store tras el await del chooser/conexión: el perfil pudo
      // borrarse o cambiar durante la espera (un closure capturado quedaría stale).
      // Leerlo del store evita actualizar/recrear un perfil obsoleto (huérfano).
      const currentPrinters = appStore.getState().webBlePrinters ?? {};
      if (printerKey && currentPrinters[printerKey]) {
        // Cambio de HARDWARE en el mismo perfil ("No aparece mi impresora" abre el chooser sin
        // filtro justo para esto): el paso que la calibración dejó bueno se validó en la
        // impresora ANTERIOR, así que deja de ser una referencia válida. El ajuste en sí se
        // conserva —el copy promete que no se pierde la configuración—, lo que se retira es la
        // AFIRMACIÓN de que ese valor ya funcionó aquí: mantenerla haría que el aviso de "Si
        // algo no funciona" empujara al operador hacia un paso nunca probado en este equipo,
        // que es exactamente el riesgo que la referencia calibrada vino a cerrar.
        const previous = currentPrinters[printerKey];
        if ((device.name ?? null) !== (previous.deviceName ?? null)) patch.calibratedSpeedStep = undefined;
        // The profile name is refreshed only if it was still the previous device's default (or
        // empty): nobody chose anything there, and keeping it would leave the profile named after
        // the printer just replaced. If the operator — or the wizard — already named it, the key is
        // OMITTED. Setting it to `undefined` would NOT preserve it: the reducer spreads the patch,
        // so that would wipe the label (it is the very mechanism the line above uses on purpose to
        // clear `calibratedSpeedStep`).
        if (isDefaultLabel(previous.label, previous.deviceName)) patch.label = defaultLabelForDevice(device.name);
        updateWebBlePrintSettings(patch, printerKey);
        return { ...previous, ...patch };
      }
      // Brand-new profile: there is no name to respect, so the default always applies. It must be
      // written here because "Agregar otra impresora" (AjustesV2) never rewrites the label
      // afterwards; the wizard does overwrite it with its per-use proposal (`presetForUse`), which
      // is the intended outcome.
      addPrinter({ ...patch, label: defaultLabelForDevice(device.name) });
      return null; // el nuevo perfil queda en el store; la UI lo re-lee del selector
    } catch (error) {
      // El usuario puede cancelar el chooser → DOMException con name 'NotFoundError'
      // (o 'AbortError' en algunos navegadores). Comparar por `name` es robusto;
      // el string del mensaje varía por navegador/idioma. No es una falla real.
      const name = error instanceof Error ? error.name : '';
      const isUserCancel = name === 'NotFoundError' || name === 'AbortError';
      if (isUserCancel) {
        // El usuario cerró el chooser sin elegir: no es falla, pero cuenta como abandono del
        // emparejamiento (numerador de la tasa de cancelación del funnel de pairing).
        emitFunnel(TRACK_EVENT_NAMES.BlePairCancelled, { reason: name });
      } else {
        capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble' });
        toast.error('No se pudo emparejar la impresora Bluetooth.');
      }
      return null;
    }
  };

  // Reconecta una impresora YA emparejada (botón "Reconectar"): primero en silencio
  // (handle en memoria / getDevices) y, si falla, abre un chooser FILTRADO a su impresora
  // dentro del gesto. Devuelve true si quedó conectada. Pensado para el caso del usuario:
  // tras recargar, getDevices puede estar ausente → un toque y listo, sin re-emparejar a ciegas.
  const reconnectPrinter = async ({
    settings,
    printerKey,
  }: {
    settings: WebBlePrintSettings;
    printerKey?: string;
  }): Promise<boolean> => {
    if (!isWebBleSupportedTransport()) {
      toast.error('Este navegador no soporta impresión por Bluetooth.');
      return false;
    }
    // CONTRATO: esta función NUNCA rechaza — resuelve true/false. La UI la invoca desde un
    // `onClick` (nadie espera la promesa), así que un rechazo quedaría huérfano en la consola.
    // `resolveDeviceOutcome` ya modela sus fallos, pero el try/catch se queda como red: si
    // mañana algo nuevo lanza (store, telemetría), el usuario ve el mismo mensaje de fallo en
    // vez de un botón colgado.
    try {
      const outcome = await resolveDeviceOutcome(settings, { allowPrompt: true, printerKey });
      if (outcome.status === 'connected') {
        toast.success('Impresora confirmada y lista.');
        return true;
      }
      // 'cancelled' (el usuario cerró el chooser) → silencio, no es una falla real.
      if (outcome.status === 'failed') {
        toast.error('No se pudo conectar. Enciende la impresora y vuelve a tocar Confirmar.');
      }
      return false;
    } catch (error) {
      capturePrintError(error, { tag: 'web_ble_printer_not_paired', trigger: 'web_ble' });
      toast.error('No se pudo conectar. Enciende la impresora y vuelve a tocar Confirmar.');
      return false;
    }
  };

  // Reconexión SILENCIOSA (sin gesto): solo intenta el camino sin chooser (handle en memoria /
  // getDevices). NUNCA abre el chooser ni muestra toasts — pensada para el warm-up al abrir
  // Ajustes. Devuelve true si quedó conectada. Si falla, no hace ruido (cae al CTA "Confirmar").
  const silentReconnect = async ({
    settings,
    printerKey,
  }: {
    settings: WebBlePrintSettings;
    printerKey?: string;
  }): Promise<boolean> => {
    if (!isWebBleSupportedTransport()) return false;
    try {
      const outcome = await resolveDeviceOutcome(settings, { allowPrompt: false, printerKey });
      return outcome.status === 'connected';
    } catch {
      return false;
    }
  };

  // Imprime el AUTO-TEST (buildPrinterDiagnostic) por la misma maquinaria (reconecta si hace
  // falta). Solo para el panel de debug: identifica en un tiro qué comando de raster materializa
  // la impresora. allowPrompt:true → puede abrir el chooser si está dormida.
  const printDiagnostic = async ({
    settings,
    printerKey,
  }: {
    settings: WebBlePrintSettings;
    printerKey?: string;
  }): Promise<boolean> => {
    // `printBytes` RE-LANZA los errores de escritura por diseño (su caller los reporta). Aquí el
    // caller es un `onClick` del panel de debug, así que cerramos el ciclo como printOrder:
    // Sentry + aviso, y false. Sin esto el rechazo quedaba huérfano en la consola.
    try {
      const bytes = buildPrinterDiagnostic(settings.pageWidth);
      return await printBytes(settings, bytes, { allowPrompt: true, printerKey });
    } catch (error) {
      capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble', extra: { diagnostic: true } });
      toast.error('Error al imprimir el diagnóstico por Bluetooth');
      return false;
    }
  };

  return {
    isWebBleSupported: isWebBleSupportedTransport(),
    isWebBleActive,
    pairPrinter,
    reconnectPrinter,
    silentReconnect,
    printOrder,
    printDiagnostic,
    printSelfServiceOrder,
    printOrderToAll,
    printSelfServiceToAll,
    ensureReady: ensureReadyTransport,
    getMainPrinter,
    status: { isPrinting, hasPairedPrinter },
  };
};
